/**
 * ScenarioWelcome - Clean 2x2 scenario selector for Impact Path
 *
 * Minimalist design with:
 * - "Kuutio" header
 * - 2x2 grid of scenario cards
 * - Line-art SVG icons
 * - Dark theme with subtle borders
 */

import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useNotice } from './DialogProvider';
import { fetchParticipantChain, getEntryPath, buildEntryUrl } from '../utils/participantEntry';
import { getBackendUrl } from '../config/api';

const API_URL = getBackendUrl();

// SVG Icons as components
const TechnologyIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="32" cy="32" r="12" />
    <circle cx="32" cy="32" r="6" />
    <path d="M32 8v8M32 48v8M8 32h8M48 32h8" />
    <path d="M15.5 15.5l5.7 5.7M42.8 42.8l5.7 5.7M15.5 48.5l5.7-5.7M42.8 21.2l5.7-5.7" />
  </svg>
);

const PopulationIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5">
    {/* Three circles for heads */}
    <circle cx="32" cy="16" r="6" />
    <circle cx="18" cy="24" r="5" />
    <circle cx="46" cy="24" r="5" />
    {/* Bodies */}
    <path d="M32 22v8M32 30c-8 0-14 6-14 14v4h28v-4c0-8-6-14-14-14z" />
    <path d="M18 29v4M18 33c-5 0-9 4-9 9v4h18" />
    <path d="M46 29v4M46 33c5 0 9 4 9 9v4H37" />
  </svg>
);

const DemocracyIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5">
    {/* Handshake */}
    <path d="M12 32c4-4 8-4 12-4h4c2 0 4 2 4 4s-2 4-4 4h-4" />
    <path d="M52 32c-4-4-8-4-12-4h-4c-2 0-4 2-4 4s2 4 4 4h4" />
    <path d="M24 36l8 8 8-8" />
    <path d="M8 28l8 4v12l-8 4" />
    <path d="M56 28l-8 4v12l8 4" />
  </svg>
);

const ClimateIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5">
    {/* Globe */}
    <circle cx="32" cy="32" r="20" />
    <ellipse cx="32" cy="32" rx="20" ry="8" />
    <path d="M32 12v40" />
    {/* Temperature indicator */}
    <circle cx="50" cy="44" r="8" />
    <path d="M50 36v4M50 48v4" />
    <circle cx="50" cy="44" r="3" fill="currentColor" />
  </svg>
);

// Styled components
const Container = styled.div`
  min-height: var(--kuutio-vh, 100vh);
  max-height: var(--kuutio-vh, 100vh);
  background: #0a0a0a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(12px, 2.5vh, 40px);
  overflow-y: auto;
  box-sizing: border-box;
`;

const Header = styled.h1`
  font-family: 'Rational', system-ui, sans-serif;
  font-size: clamp(28px, 5vh, 48px);
  font-weight: 700;
  color: #666;
  margin-bottom: clamp(20px, 4vh, 60px);
  letter-spacing: 2px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: clamp(12px, 2vh, 24px);
  max-width: min(800px, 100%);
  width: 100%;
`;

const Card = styled.div`
  background: transparent;
  border: 1px solid #333;
  border-radius: 12px;
  padding: clamp(20px, 3vh, 48px) clamp(16px, 2vw, 32px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 0;

  &:hover {
    border-color: #555;
    background: rgba(255, 255, 255, 0.02);
  }

  svg {
    color: #555;
    margin-bottom: 24px;
    transition: color 0.2s ease;
  }

  &:hover svg {
    color: #888;
  }
`;

const CardTitle = styled.h2`
  font-family: 'Rational', system-ui, sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #aaa;
  margin: 0;
  letter-spacing: 0.5px;
`;

const Loading = styled.div`
  color: #666;
  font-size: 18px;
`;

// Icon mapping
const iconMap = {
  'Teknologia': TechnologyIcon,
  'Väestö': PopulationIcon,
  'Demokratia': DemocracyIcon,
  'Ilmasto': ClimateIcon,
  // Fallbacks
  'default': TechnologyIcon
};

const ScenarioWelcome = ({ instanceId: propInstanceId, onSelect }) => {
  const { t } = useTranslation();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const notice = useNotice();

  const instanceId = propInstanceId || searchParams.get('kid') || searchParams.get('instance');

  useEffect(() => {
    if (!instanceId) {
      setLoading(false);
      return;
    }

    const fetchScenarios = async () => {
      try {
        const scenariosRes = await fetch(`${API_URL}/api/scenarios/${instanceId}`);
        if (scenariosRes.ok) {
          const data = await scenariosRes.json();
          const sorted = (data.scenarios || []).sort((a, b) =>
            (a.order_index || 0) - (b.order_index || 0)
          );
          setScenarios(sorted);
        }
      } catch (err) {
        console.error('Error fetching scenarios:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchScenarios();
  }, [instanceId]);

  const handleSelectScenario = async (scenario) => {
    if (onSelect) {
      onSelect(scenario);
      return;
    }
    const participantId = searchParams.get('participant') || crypto.randomUUID();
    try {
      const chain = await fetchParticipantChain(instanceId);
      // Impact path starts in Omat for the chosen scenario; the systemic map
      // opens in Kaikki, where the scenario is only a filter.
      navigate(buildEntryUrl(chain, instanceId, {
        scenario: scenario.id,
        participant: participantId,
        ...(getEntryPath(chain) === '/impactpath' ? { type: '1' } : {}),
      }));
    } catch (err) {
      console.error('Participant entry failed:', err);
      notice.error(err.message);
    }
  };

  if (loading) {
    return (
      <Container>
        <Loading>{t('common.loading')}</Loading>
      </Container>
    );
  }

  if (!instanceId) {
    return (
      <Container>
        <Header>Kuutio</Header>
        <Loading>{t('scenarioWelcome.missingInstance')}</Loading>
      </Container>
    );
  }

  return (
    <Container>
      <Header>Kuutio</Header>
      <Grid>
        {scenarios.map((scenario) => {
          const IconComponent = iconMap[scenario.title] || iconMap.default;
          return (
            <Card key={scenario.id} onClick={() => handleSelectScenario(scenario)}>
              <IconComponent />
              <CardTitle>{scenario.title}</CardTitle>
            </Card>
          );
        })}
      </Grid>
    </Container>
  );
};

export default ScenarioWelcome;
